import {Typography} from 'onboarding-components';
import * as React from 'react';
import map from 'lodash/map';
import clsx from 'clsx';

import {SelectReact} from 'common/components/select';
import {SelectTheme} from 'common/components/select/constants';
import {SvgIconReact} from 'common/components/svg-icon';
import {IMMERSIVE_ASSETS} from 'common/onboarding/immersive-onboarding/assets';
import {
  DEFAULT_INDUSTRY_KEY,
  EVENT_INPUT_MAX,
  INDUSTRY_EVENTS,
  PROPERTIES,
} from 'common/onboarding/immersive-onboarding/constants';
import {TooltipReact} from 'common/components/tooltip';
import {Placement} from 'common/components/tooltip/types';
import {Banner, BannerType} from 'common/onboarding/immersive-onboarding/components/banner';
import {Content, Header} from 'common/onboarding/immersive-onboarding/components';
import {TextInput} from 'common/onboarding/immersive-onboarding/components/text-input';
import {
  AppContext,
  BreakdownType,
  IndustryKey,
  SetupGuideContext,
  Step,
  SubStep,
  VisibilityContext,
} from 'common/onboarding/immersive-onboarding/types';
import {promptIntroModal} from 'common/widgets/intro';
import {track, TrackingEvent, TrackingProperty} from 'common/onboarding/immersive-onboarding/tracking';
import {Permission} from 'common/report/constants';
import type {ChromeHeader} from 'common/widgets/chrome/chrome-header';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {TileButton} from 'common/onboarding/immersive-onboarding/components/tile-button';
import {scrollToStep} from 'common/onboarding/immersive-onboarding/components/scroll-manager';
import rocketImg from 'common/onboarding/immersive-onboarding/assets/rocket.svg';
import {ApiClient} from 'common/onboarding/immersive-onboarding/api-client';

import {Section} from '../section/section';
import {setupCTA, WelcomeV1} from '../welcome/welcome-v1';
import {EventChartPreviewCarousel, PropertyBreakdownPreviewCarousel} from './chart-preview';
import {ReadyToConnect} from './substeps';
import {EventSubStepContext} from './common';
import styles from './events.module.styl';

const scrollToSubStep = (subStep: SubStep) => {
  scrollToStep(subStep);
};

const EventBanner = () => {
  const {currentUser} = React.useContext(AppContext);

  return (
    <Banner type={BannerType.Marketing}>
      <Banner.Content title={`New to event based analytics?`}>
        <Banner.Image src={rocketImg} height={40} width={31} />
      </Banner.Content>
      <Banner.Action
        actionName={`Learn how it works`}
        action={() => {
          promptIntroModal({
            'first-name': (currentUser.get().value?.name || ``).split(` `)[0],
          });
          track(TrackingEvent.EventsLearnMoreClick);
        }}
      />
    </Banner>
  );
};

const EventSelector = ({selected, onIndustryKeyChange, events, updateEvents}) => {
  const dropdownOptions = Object.keys(INDUSTRY_EVENTS).map((el) => ({value: el, label: INDUSTRY_EVENTS[el].name}));

  const deleteEvent = (eventIndex) => {
    updateEvents((events) => events.filter((_, index) => index !== eventIndex));
  };

  const inputValidator = (val) => {
    return val.trim().length > 0;
  };

  return (
    <div className={styles.sectionTile}>
      <SelectReact
        sections={[{items: dropdownOptions}]}
        disableSearch
        onChange={(ev) => {
          onIndustryKeyChange({industryKey: ev.detail.item.value as IndustryKey});
        }}
        theme={SelectTheme.Tertiary}
        alwaysShowCaret
        menuWidth={150}
      >
        <div slot={`trigger`}>
          <Typography weight='regular' variant='bodySm' style={{fontSize: 14}}>
            {INDUSTRY_EVENTS[selected].name} <SvgIconReact icon='caret-down' style={{minHeight: 17, height: 17}} />
          </Typography>
        </div>
      </SelectReact>
      <div className={styles.eventContainer}>
        {events.map((event, eventIndex) => (
          <div key={`${INDUSTRY_EVENTS[selected].name}-${event}`} className={styles.eventEditorRow}>
            <TextInput
              validator={inputValidator}
              hideValidCheckMark
              defaultValue={event}
              onBlur={(value) => {
                updateEvents((events) => events.map((event, index) => (index === eventIndex ? value : event)));
              }}
            />
            <div
              className={clsx(styles.deleteIconWrapper, {[styles.disabled]: events.length === 1 && eventIndex === 0})}
              onClick={() => events.length > 1 && deleteEvent(eventIndex)}
            >
              <SvgIconReact className={styles.deleteIcon} icon={`trashcan`} />
              {events.length === 1 && eventIndex === 0 && (
                <TooltipReact placement={Placement.Top} text={`You must choose at least one event`} />
              )}
            </div>
          </div>
        ))}
        {events.length < EVENT_INPUT_MAX && (
          <TextInput
            key={Math.random()} // Hack to trick React rendering...
            validator={inputValidator}
            hideValidCheckMark
            placeholder={`Add Your Own Event`}
            onBlur={(value) => {
              if (value.trim().length > 0) {
                updateEvents((events) => [...events, value.trim()]);
              }
            }}
            style={{width: `calc(100% - 38px)`}} // 38px is the width of delete icon + gap from container
          />
        )}
        {/* -1 due to existing input box (5 events + 1 event input) */}
        {events.length >= EVENT_INPUT_MAX - 1 && (
          <Typography variant='captionSm' className={styles.eventLimitText}>
            We suggest tracking 6 or fewer events to start
          </Typography>
        )}
      </div>
    </div>
  );
};

const WelcomeSection = () => {
  const {currentProject} = React.useContext(AppContext);
  const {setEventSubStep} = React.useContext(EventSubStepContext);

  const {userCount} = currentProject.get().value || {};

  return (
    // TODO: After v1.1/v2 is GA, remove this block
    <Section
      subStepId={SubStep.Welcome}
      className={styles.welcomePadding}
      title={`Welcome to Mixpanel!`}
      subTitle={`Mixpanel will help you better understand your customers and answer questions about your product`}
      onNext={() => {
        track(TrackingEvent.SubStepCompleted, {[TrackingProperty.SubStepName]: `Welcome`});
        setEventSubStep(SubStep.EventConfirmation);
      }}
      nextCTA={setupCTA(userCount)}
    >
      <WelcomeV1 />
    </Section>
  );
};

const ConfirmEventsSection = () => {
  const {setupGuideInfo, setSetupGuideInfo} = React.useContext(SetupGuideContext);
  const {setEventSubStep} = React.useContext(EventSubStepContext);

  const [selected, updateSelected] = React.useState<IndustryKey>(DEFAULT_INDUSTRY_KEY);
  const [events, updateEvents] = React.useState<string[]>(INDUSTRY_EVENTS[DEFAULT_INDUSTRY_KEY].events);

  React.useEffect(() => {
    if (setupGuideInfo !== null) {
      updateSelected(setupGuideInfo.industry);
      updateEvents(
        setupGuideInfo.events.length > 0 ? setupGuideInfo.events : INDUSTRY_EVENTS[setupGuideInfo.industry].events,
      );
    }
  }, [setupGuideInfo]);

  const onIndustryKeyChange = ({industryKey}: {industryKey: IndustryKey}) => {
    updateSelected(industryKey);
    if (setupGuideInfo.events.length === 0 || setupGuideInfo.industry !== industryKey) {
      updateEvents(INDUSTRY_EVENTS[industryKey].events);
    } else updateEvents(setupGuideInfo.events);
  };

  return (
    <Section
      scrollable
      subStepId={SubStep.EventConfirmation}
      sectionLabel='1'
      title={`With the right events, it’s easy to learn about your product`}
      subTitle={`The events below are popular selections for your industry. Customize them to fit your needs and don’t worry, you can always change them later.`}
      onNext={() => {
        ApiClient.updateSetupGuideInfo(setupGuideInfo.projectId, selected, events).then((result) => {
          setSetupGuideInfo(result);
        });

        track(TrackingEvent.SubStepCompleted, {[TrackingProperty.SubStepName]: `Event confirmation`});
        setEventSubStep(SubStep.Segmentation);
      }}
      skipCTA={`Skip for now`}
      onSkip={() => {
        track(TrackingEvent.SubStepSkipped, {[TrackingProperty.SubStepName]: `Event confirmation`});
        setEventSubStep(SubStep.ReadyToConnect);
      }}
      headerComponent={<EventBanner />}
    >
      <div className={styles.breakdownContent}>
        <div className={styles.selector}>
          <EventSelector
            selected={selected}
            events={events}
            onIndustryKeyChange={onIndustryKeyChange}
            updateEvents={updateEvents}
          />
        </div>
        <div className={styles.preview}>
          <EventChartPreviewCarousel events={events} />
        </div>
      </div>
    </Section>
  );
};

const BreakdownPreviewSection = () => {
  const {setupGuideInfo} = React.useContext(SetupGuideContext);
  const [selectedProperty, setSelectedProperty] = React.useState<BreakdownType>(`device_type`);
  const {setEventSubStep} = React.useContext(EventSubStepContext);

  return (
    <Section
      scrollable
      sectionLabel='2'
      subStepId={SubStep.Segmentation}
      title='Properties help you segment your data'
      subTitle='Most mixpanel integration methods include default event properties, such as “Country”. You can use these properties to uncover trends. Try the example below:'
      onNext={() => {
        track(TrackingEvent.SubStepCompleted, {[TrackingProperty.SubStepName]: `Property breakdown`});
        setEventSubStep(SubStep.ReadyToConnect);
      }}
    >
      <div className={styles.breakdownContent}>
        <div className={styles.selector}>
          {map(PROPERTIES, (value, key: BreakdownType) => {
            return (
              <TileButton
                style={{width: `100%`}}
                key={key}
                onClick={() => setSelectedProperty(key)}
                selected={selectedProperty === key}
              >
                {value}
              </TileButton>
            );
          })}
          <span>
            These are sample default properties.{` `}
            <a
              href={`https://docs.mixpanel.com/docs/tracking/how-tos/events-and-properties`}
              target={`_blank`}
              onClick={() => {
                track(TrackingEvent.LearnMoreClick);
              }}
            >{`Learn more.`}</a>
          </span>
        </div>
        <div className={styles.preview}>
          <PropertyBreakdownPreviewCarousel events={setupGuideInfo?.events ?? []} property={selectedProperty} />
        </div>
      </div>
    </Section>
  );
};

const InviteFooter = () => {
  const {setVisibility} = React.useContext(VisibilityContext);
  const {currentUser, currentProject} = React.useContext(AppContext);
  const {organizationId} = currentProject.get().value ?? {};
  const canInviteUsers = currentUser
    .get()
    .value?.organizations?.[organizationId]?.permissions.includes(Permission.AddUser);

  return (
    <>
      {`Need help? `}
      <a
        onClick={() => {
          if (canInviteUsers) {
            setVisibility(false);

            const chromeHeader = document.querySelector<ChromeHeader>(`mp-chrome-header`);
            chromeHeader.helpers.openInviteUserModal(() => setVisibility(true));
          } else {
            addToast({
              type: ToastType.ERROR,
              message: `You don’t have access to inviting. Ask a teammate to change your account’s permissions.`,
            });
          }

          track(TrackingEvent.InviteTeammate);
        }}
      >{`Invite a teammate`}</a>
      {` — you have unlimited seats.`}
    </>
  );
};

const ReadyToConnectSection = () => {
  return (
    <Section
      scrollable
      sectionLabel='3'
      subStepId={SubStep.ReadyToConnect}
      title={`Great work! You’re ready to connect your data`}
      subTitle={<InviteFooter />}
    >
      <ReadyToConnect />
    </Section>
  );
};

const ContentV1 = () => {
  return (
    <>
      <WelcomeSection />
      <ConfirmEventsSection />
      <BreakdownPreviewSection />
      <ReadyToConnectSection />
    </>
  );
};

export const EventsV1 = () => {
  const [eventSubStep, setEventSubStep] = React.useState<SubStep>(SubStep.Welcome);
  const {completed} = React.useContext(SetupGuideContext);

  React.useEffect(() => {
    // Only scroll via effect if it's not the first subStep
    if (eventSubStep && eventSubStep !== SubStep.Welcome) scrollToSubStep(eventSubStep);
  }, [eventSubStep]);

  return (
    <EventSubStepContext.Provider value={{eventSubStep, setEventSubStep}}>
      <Header
        title={`Personalize your setup`}
        step={`step 1`}
        imgSrc={IMMERSIVE_ASSETS.toggles}
        duration={3}
        completed={completed[Step.Events]}
      />
      <Content>
        <ContentV1 />
      </Content>
    </EventSubStepContext.Provider>
  );
};
